import { Flex, NumberInput, Select, SelectItem, Text } from '@tremor/react'
import { useState } from 'react'
import { generateVMs } from '.'

interface ICalculator {
    className?: string
    factor?: number
    constantPrice?: number
    constantPrice2?: number
    hasIpCost?: boolean
    hasNetworkCost?: boolean
}
export default function Calculator({
    className,
    factor = 1.0,
    constantPrice = 0.0,
    constantPrice2 = 0.0,
    hasIpCost = true,
    hasNetworkCost = true,
}: ICalculator) {
    const [computeId, setComputeID] = useState<string>(
        'VMType_Burstable_2core_4GB'
    )
    const [network, setNetwork] = useState<number>(100)
    const [instanceCount, setInstanceCount] = useState<number>(1)
    const [disk, setDisk] = useState<number>(50)
    const [ip, setIP] = useState<string>('shared')
    const [commitment, setCommitment] = useState<string>('6months')
    const networkCost = () => {
        if (!hasNetworkCost) return 0
        return network * 0.032623
    }
    const ipCost = () => {
        if (!hasIpCost) return 0

        switch (ip) {
            case 'shared':
                return 0.0
            case 'dedicated':
                return 0.002584
            default:
                return 0
        }
    }
    const diskCost = () => {
        switch (commitment) {
            case 'onDemand':
                return 0.000087 * disk
            case '1month':
                return 0.000082 * disk
            case '6months':
                return 0.000077 * disk
            default:
                return 0
        }
    }

    const vmCost = () => {
        switch (commitment) {
            case 'onDemand':
                return (
                    (generateVMs()
                        .filter((v) => v.id === computeId)
                        .at(0)?.priceOnDemand || 0) * factor
                )
            case '1month':
                return (
                    (generateVMs()
                        .filter((v) => v.id === computeId)
                        .at(0)?.priceOneMonth || 0) * factor
                )
            case '6months':
                return (
                    (generateVMs()
                        .filter((v) => v.id === computeId)
                        .at(0)?.priceSixMonths || 0) * factor
                )
            default:
                return 0
        }
    }

    const constVMCost = () => {
        switch (commitment) {
            case 'onDemand':
                return (generateVMs()
                    .filter((v) => v.id === computeId)
                    .at(0)?.cpu || 0) < 100
                    ? constantPrice
                    : constantPrice2
            case '1month':
                return (generateVMs()
                    .filter((v) => v.id === computeId)
                    .at(0)?.cpu || 0) < 100
                    ? constantPrice
                    : constantPrice2
            case '6months':
                return (generateVMs()
                    .filter((v) => v.id === computeId)
                    .at(0)?.cpu || 0) < 100
                    ? constantPrice
                    : constantPrice2
            default:
                return 0
        }
    }

    const price = () => {
        return `Total monthly cost: ${(
            networkCost() +
            diskCost() * instanceCount * 720 +
            vmCost() * instanceCount * 720 +
            ipCost() * instanceCount * 720 +
            constVMCost() * 720
        ).toFixed(3)} OMR`
    }

    const filteredVMS = generateVMs().filter((v) => {
        switch (commitment) {
            case 'onDemand':
                return v.priceOnDemand !== undefined
            case '1month':
                return v.priceOneMonth !== undefined
            case '6months':
                return v.priceSixMonths !== undefined
            default:
                return false
        }
    })

    const titleClassname = 'w-3/12'
    const inputClassname = 'w-6/12'
    const priceClassname = 'w-3/12 text-start pl-2'

    return (
        <Flex
            flexDirection="col"
            justifyContent="start"
            className={`space-y-4 ${className} min-h-96`}
        >
            <Flex flexDirection="row">
                <Text className={titleClassname}>Commitment</Text>
                <Select
                    className={inputClassname}
                    value={commitment}
                    onValueChange={setCommitment}
                >
                    <SelectItem value="onDemand">
                        On Demand (No commitment)
                    </SelectItem>
                    <SelectItem value="1month">1-Month</SelectItem>
                    <SelectItem value="6months">6-Months</SelectItem>
                </Select>
                <div className={priceClassname} />
            </Flex>
            <Flex flexDirection="row">
                <Text className={titleClassname}>No of instances</Text>
                <NumberInput
                    className={inputClassname}
                    min={1}
                    max={100}
                    value={instanceCount}
                    onValueChange={setInstanceCount}
                />
                <div className={priceClassname} />
            </Flex>
            <Flex flexDirection="row">
                <Text className={titleClassname}>Compute</Text>
                <Select
                    className={inputClassname}
                    value={computeId}
                    onValueChange={setComputeID}
                    error={
                        computeId === '' ||
                        filteredVMS.filter((v) => v.id === computeId).length ===
                            0
                    }
                >
                    {filteredVMS.map((v) => (
                        <SelectItem value={v.id}>
                            <Flex flexDirection="col">
                                <Text>
                                    CPU: {v.cpu} cores
                                    {v.shared ? ' (shared)' : ''}, Memory:{' '}
                                    {v.ram} GB
                                    {v.gpu ? ', GPU: Yes' : ''}
                                </Text>
                            </Flex>
                        </SelectItem>
                    ))}
                </Select>
                <Text className={priceClassname}>
                    {vmCost().toFixed(6)} OMR per hour
                </Text>
            </Flex>
            {hasIpCost && (
                <Flex flexDirection="row">
                    <Text className={titleClassname}>IP Address</Text>
                    <Select
                        className={inputClassname}
                        value={ip}
                        onValueChange={setIP}
                    >
                        <SelectItem value="dedicated">
                            Dedicated IPv4
                        </SelectItem>
                        <SelectItem value="shared">Shared IPv4</SelectItem>
                    </Select>
                    <Text className={priceClassname}>
                        {ipCost().toFixed(6)} OMR per hour
                    </Text>
                </Flex>
            )}
            {hasNetworkCost && (
                <Flex flexDirection="row">
                    <Text className={titleClassname}>
                        Monthly download + upload (GB)
                    </Text>
                    <NumberInput
                        className={inputClassname}
                        min={0}
                        max={10000}
                        value={network}
                        onValueChange={setNetwork}
                    />
                    <Text className={priceClassname}>
                        {networkCost().toFixed(6)} OMR
                    </Text>
                </Flex>
            )}
            <Flex flexDirection="row">
                <Text className={titleClassname}>SSD Disk (GB)</Text>
                <NumberInput
                    min={8}
                    className={inputClassname}
                    max={4096}
                    value={disk}
                    onValueChange={setDisk}
                />
                <Text className={priceClassname}>
                    {diskCost().toFixed(6)} OMR per hour
                </Text>
            </Flex>
            {constantPrice > 0 && (
                <Flex flexDirection="row">
                    <Text className={titleClassname}>
                        Cluster Management cost
                    </Text>
                    <div />
                    <Text className={priceClassname}>
                        {constantPrice.toFixed(6)} OMR per hour
                    </Text>
                </Flex>
            )}
            <Flex flexDirection="row" justifyContent="end">
                <Text className="!text-lg font-bold my-4">{price()}</Text>
            </Flex>
        </Flex>
    )
}
